import { Circle, Container, Graphics, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import { WEAPONS } from '@/config/weapons';
import { weaponCrateKey, weaponGlyphKey } from '@/config/weapon-visuals';
import { drawCardIcon } from './card-icons';
import {
  arrowGlyph, crossGlyph, deltaGlyph, padlockGlyph, plusGlyph, sparkleGlyph, swapGlyph,
} from './hangar-icons';
import { scrapGems } from './weapon-labels';
import { chamferPanel, hexPath, label, targetBrackets } from './widgets';

/*
 * Hangar tiles (docs/09 wave 4): numbered socket tiles, bag tiles, the bag
 * padlock and the swap-or-scrap crate. Wordless first — glyph, number,
 * colour, arrow; any text is secondary. Geometry lives in shop-hangar.ts.
 */

export const SOCKET_TILE = { w: 124, h: 150 } as const;
export const BAG_TILE = { w: 104, h: 100 } as const;
export const CRATE_TILE = { w: 170, h: 208 } as const;
export const SCRAP_BADGE = { x: 18, y: 16, r: 13 } as const;

const INK = 0x0f0c1a;
const DIM = 0x4a4256;

/** Socket tile state: what sits in socket `index` and how it is lit. */
export interface SocketTileView {
  index: number;
  weaponId: string | null;
  open: boolean;
  /** Padlock price (next socket only; null = chained / not for sale) */
  price: number | null;
  affordable: boolean;
  power: number;
  selected: boolean;
  /** Where the focused bag weapon would go (yellow brackets) */
  target: boolean;
  fresh: boolean;
}

function gemPrice(c: Container, x: number, y: number, price: number, red: boolean, on: boolean): void {
  const gem = new Sprite(getTexture(red ? 'pickup/gem-red' : 'pickup/gem-yellow'));
  gem.anchor.set(1, 0.5);
  gem.scale.set(0.6);
  gem.position.set(x - 2, y);
  const txt = label(String(price), 20, red ? COLORS.gemRed : COLORS.gemYellow, 1);
  txt.anchor.set(0, 0.5);
  txt.position.set(x + 2, y);
  gem.alpha = txt.alpha = on ? 1 : 0.45;
  c.addChild(gem, txt);
}

function brackets(c: Container, w: number, h: number, sel: boolean, target: boolean): void {
  if (!sel && !target) return;
  const g = new Graphics();
  if (target) targetBrackets(g, w, h, COLORS.playerYellow, 14, sel ? 10 : 5);
  if (sel) targetBrackets(g, w, h, COLORS.uiAccent, 16, 4);
  c.addChild(g);
}

export function buildSocketTile(v: SocketTileView): Container {
  const { w, h } = SOCKET_TILE;
  const c = new Container();
  const def = v.weaponId ? WEAPONS[v.weaponId] : undefined;
  const cat = def ? WEAPON_CATEGORY_COLOR[def.category] : DIM;
  const g = new Graphics();
  chamferPanel(g, w, h, {
    fill: def ? 0x15111f : v.open ? 0x110e1b : 0x0d0a16,
    stroke: def ? cat : v.target ? COLORS.playerYellow : v.price !== null ? 0x5a2a36 : 0x2a2440,
    strokeWidth: def ? 4 : 2, notch: 14,
  });
  // The socket's number — the same 1-2-3 as the HUD strip
  hexPath(g, 22, 22, 16).fill(v.target ? COLORS.playerYellow : def ? cat : v.open ? DIM : 0x2a2440);
  c.addChild(g);
  const num = label(String(v.index + 1), 20, v.open || v.target ? INK : 0x5a6168, 0);
  num.anchor.set(0.5);
  num.position.set(22, 23);
  c.addChild(num);

  const m = new Graphics();
  c.addChild(m);
  if (def) {
    const glyph = new Sprite(getTexture(weaponGlyphKey(def.id)));
    glyph.anchor.set(0.5);
    glyph.position.set(w / 2, 72);
    c.addChild(glyph);
    drawCardIcon(m, 'damage', COLORS.statPower).scale.set(0.42);
    m.position.set(w / 2 - 30, 128);
    const p = label(String(Math.round(v.power)), 20, 0xffffff, 1);
    p.anchor.set(0, 0.5);
    p.position.set(w / 2 - 16, 128);
    c.addChild(p);
    if (v.fresh) {
      const sp = sparkleGlyph(new Graphics(), w - 18, 20, 9, COLORS.playerYellow);
      c.addChild(sp);
    }
  } else if (v.open) {
    plusGlyph(m, w / 2, 76, 20, v.target ? COLORS.playerYellow : DIM);
  } else if (v.price !== null) {
    padlockGlyph(m, w / 2, 66, 22, v.affordable ? COLORS.playerCream : 0x5a6168);
    gemPrice(c, w / 2, 122, v.price, true, v.affordable);
  } else {
    padlockGlyph(m, w / 2, 72, 16, 0x3a3548);
  }
  brackets(c, w, h, v.selected, v.target);
  return c;
}

/** Bag tile state. */
export interface BagTileView {
  weaponId: string;
  selected: boolean;
  fresh: boolean;
  /** Ship power change if equipped into its target socket */
  delta: number;
  /** ✕ badge drawn (touch: always; pad/keys: focused tile only) */
  showScrap: boolean;
  /** First scrap press done — the next one confirms */
  armed: boolean;
}

/**
 * Red ✕ + gem + value: "this becomes gems". `hitR` widens the tap circle
 * beyond the drawn badge (phone shop: ≥ 44 CSS px).
 */
export function scrapBadge(
  c: Container, weaponId: string, armed: boolean, onScrap: () => void,
  at: { x: number; y: number } = SCRAP_BADGE, hitR = 0,
): void {
  const b = new Container();
  const g = new Graphics();
  g.circle(0, 0, SCRAP_BADGE.r).fill(armed ? COLORS.dangerRed : 0x1d1430)
    .stroke({ color: COLORS.dangerRed, width: 2 });
  crossGlyph(g, 0, 0, 5, armed ? 0xffffff : COLORS.dangerRed);
  b.addChild(g);
  b.position.set(at.x, at.y);
  if (hitR > 0) b.hitArea = new Circle(0, 0, hitR);
  if (armed) {
    const gem = new Sprite(getTexture('pickup/gem-yellow'));
    gem.anchor.set(0, 0.5);
    gem.scale.set(0.45);
    gem.position.set(16, 0);
    const v = label(`+${scrapGems(weaponId)}`, 15, COLORS.gemYellow, 1);
    v.anchor.set(0, 0.5);
    v.position.set(36, 0);
    b.addChild(gem, v);
  }
  b.eventMode = 'static';
  b.cursor = 'pointer';
  b.on('pointertap', (e) => {
    e.stopPropagation();
    onScrap();
  });
  c.addChild(b);
}

export function buildBagTile(v: BagTileView, onScrap: () => void): Container {
  const { w, h } = BAG_TILE;
  const c = new Container();
  const def = WEAPONS[v.weaponId]!;
  const cat = WEAPON_CATEGORY_COLOR[def.category];
  const g = new Graphics();
  chamferPanel(g, w, h, {
    fill: v.armed ? 0x2a1018 : 0x15111f,
    stroke: v.armed ? COLORS.dangerRed : cat,
    strokeWidth: 3, notch: 10, plain: true,
  });
  c.addChild(g);
  const glyph = new Sprite(getTexture(weaponGlyphKey(def.id)));
  glyph.anchor.set(0.5);
  glyph.scale.set(0.82);
  glyph.position.set(w / 2, 42);
  if (v.armed) glyph.alpha = 0.4;
  c.addChild(glyph);
  if (v.fresh) c.addChild(sparkleGlyph(new Graphics(), w - 15, 15, 8, COLORS.playerYellow));
  // ▲ / ▼ — better or worse for the ship than what sits in its target socket
  const flat = Math.abs(v.delta) < 0.5;
  const color = flat ? 0x8a9099 : v.delta > 0 ? COLORS.statUp : COLORS.statDown;
  const d = new Graphics();
  if (!flat) deltaGlyph(d, w / 2 - 24, 84, 7, color, v.delta > 0);
  c.addChild(d);
  const txt = label(flat ? '=' : `${v.delta > 0 ? '+' : ''}${Math.round(v.delta)}`, 16, color, 1);
  txt.anchor.set(0, 0.5);
  txt.position.set(w / 2 - 12, 84);
  c.addChild(txt);
  if (v.showScrap || v.armed) scrapBadge(c, v.weaponId, v.armed, onScrap);
  brackets(c, w, h, v.selected, false);
  return c;
}

export function buildEmptyBagTile(): Container {
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, BAG_TILE.w, BAG_TILE.h, {
    fill: 0x110e1b, fillAlpha: 0.6, stroke: 0x2a2440, strokeWidth: 2, notch: 10, plain: true,
  });
  c.addChild(g);
  return c;
}

/** The bag's padlock: one more slot for yellow. */
export function buildBagBuyTile(price: number, affordable: boolean, selected: boolean): Container {
  const { w, h } = BAG_TILE;
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, h, { fill: 0x0d0a16, stroke: 0x4a3c1e, strokeWidth: 2, notch: 10, plain: true });
  padlockGlyph(g, w / 2, 38, 16, affordable ? COLORS.playerCream : 0x5a6168);
  c.addChild(g);
  gemPrice(c, w / 2, 78, price, false, affordable);
  brackets(c, w, h, selected, false);
  return c;
}

/** Swap-or-scrap crate (full bag): ⇄ into the target socket, or ✕ for gems. */
export function buildCrateTile(
  weaponId: string, target: number, selected: boolean,
  onSwap: () => void, onScrap: () => void,
): Container {
  const { w, h } = CRATE_TILE;
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, h, { fill: 0x1c120f, stroke: COLORS.dangerOrange, strokeWidth: 4, notch: 14 });
  c.addChild(g);
  const crate = new Sprite(getTexture(weaponCrateKey(weaponId)));
  crate.anchor.set(0.5);
  crate.scale.set(1.15);
  crate.position.set(w / 2, 56);
  c.addChild(crate);
  c.addChild(sparkleGlyph(new Graphics(), w - 20, 20, 9, COLORS.playerYellow));

  const button = (y: number, primary: boolean, draw: (b: Container) => void, act: () => void) => {
    const b = new Container();
    const bg = new Graphics();
    chamferPanel(bg, w - 24, 40, {
      fill: primary ? (selected ? 0x1d4a44 : 0x16332f) : 0x24121a,
      stroke: primary ? (selected ? COLORS.uiAccent : 0x2e8c80) : COLORS.dangerRed,
      strokeWidth: primary && selected ? 3 : 2, notch: 10, plain: true,
    });
    b.addChild(bg);
    draw(b);
    b.position.set(12, y);
    b.eventMode = 'static';
    b.cursor = 'pointer';
    b.on('pointertap', (e) => {
      e.stopPropagation();
      act();
    });
    c.addChild(b);
  };
  button(110, true, (b) => {
    const m = new Graphics();
    swapGlyph(m, 34, 20, 11, 0xffffff);
    arrowGlyph(m, 70, 20, 8, 0xe8eaf0);
    hexPath(m, 100, 20, 14).fill(COLORS.playerYellow);
    b.addChild(m);
    const n = label(String(target + 1), 17, INK, 0);
    n.anchor.set(0.5);
    n.position.set(100, 21);
    b.addChild(n);
  }, onSwap);
  button(158, false, (b) => {
    const m = crossGlyph(new Graphics(), 30, 20, 7, COLORS.dangerRed);
    b.addChild(m);
    const gem = new Sprite(getTexture('pickup/gem-yellow'));
    gem.anchor.set(0.5);
    gem.scale.set(0.5);
    gem.position.set(66, 20);
    const v = label(`+${scrapGems(weaponId)}`, 17, COLORS.gemYellow, 1);
    v.anchor.set(0, 0.5);
    v.position.set(82, 20);
    b.addChild(gem, v);
  }, onScrap);
  if (selected) {
    const br = new Graphics();
    targetBrackets(br, w, h, COLORS.uiAccent, 16, 4);
    c.addChild(br);
  }
  return c;
}
